import { useEffect } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BankTransferInstructionsModal } from "@pages/BillsPage/components/BillsPayment/components/BankTransferInstructionsModal/BankTransferInstructionsModal";
import { BillErrorsNotification } from "@pages/BillsPage/components/BillsPayment/components/MeterReadingErrorsNotification/BillErrorsNotification";
import { PaperBillPaymentModal } from "@pages/BillsPage/components/BillsPayment/components/PaperBillPaymentModal/PaperBillPaymentModal";
import { AMLModal } from "@features/AMLModal/AMLModal";
import { KYCWarningModal } from "@features/KYCWarningModal/KYCWarningModal";
import { PaymentRestrictionModal } from "@features/PaymentRestrictionModal/PaymentRestrictionModal";
import { Button } from "@components/Button";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import {
  useApiErrorUtils,
  type IValidationError,
} from "@hooks/useApiErrorUtils";
import { useJointBillErrorsStore } from "@store/jointBillErrors/jointBillErrors";
import { useModalStore } from "@store/modal/modal";
import { useCustomerMetadataQuery } from "@services/customer/queries";
import { type IOverviewJointBillService } from "@services/overview/types";
import { useInitiatePaymentMutation } from "@services/payments/mutations";
import {
  useGetJointBillServicesExpectedPaymentAmount,
  usePaymentTaxCollectorTypes,
} from "@services/payments/queries";
import {
  BeforePaymentAction,
  PaymentRestrictionType,
  PaymentType,
  type IBankTransferPayment,
  type IFakePayment,
  type IOnlinePayment,
  type IPaperBillPayment,
} from "@services/payments/types";
import { isNullOrUndefined } from "@utils/common";
import { formatPrice } from "@utils/units";
import { FakePaymentInformationCard } from "./components/FakePaymentInformationCard/FakePaymentInformationCard";
import { SelectedTaxCollectorDisplay } from "./components/SelectedTaxCollectorDisplay/SelectedTaxCollectorDisplay";
import { TaxCollectorRadio } from "./components/TaxCollectorRadio/TaxCollectorRadio";

interface IBillsFooterProps {
  periodId: number;
  selectedJointBillServiceIds: IOverviewJointBillService["jointBillServiceId"][];
}

export const BillsPayment: React.FC<IBillsFooterProps> = ({
  periodId,
  selectedJointBillServiceIds,
}) => {
  const { create } = useModalStore();
  const { t } = useTranslation();
  const {
    meterReadingErrors,
    setMeterReadingErrors,
    clearMeterReadingErrors,
    setBillNumberErrors,
    clearBillNumberErrors,
    billNumberErrors,
  } = useJointBillErrorsStore();
  const { getApiErrors } = useApiErrorUtils();
  const { data: paymentTaxCollectorData } = usePaymentTaxCollectorTypes();
  const { data: totalAmount, isFetching: isTotalAmountLoading } =
    useGetJointBillServicesExpectedPaymentAmount({
      periodId,
      selectedJointBillServiceIds,
    });
  const { data: customerMetadata } = useCustomerMetadataQuery();
  const {
    mutateAsync: initiatePayment,
    isPending: isInitiatePaymentLoading,
    data: paymentInitiationData,
  } = useInitiatePaymentMutation();
  const isUpdatingSelectedTaxCollector = useIsMutating({
    mutationKey: ["payment", "selectedTaxCollectorType"],
  });
  const isLoading =
    isTotalAmountLoading ||
    isInitiatePaymentLoading ||
    Boolean(isUpdatingSelectedTaxCollector);

  const currentPaymentTaxCollector =
    paymentTaxCollectorData?.taxCollectorTypes.find(
      (taxCollectorType) => taxCollectorType.isChecked,
    );

  const openBankTransferInstructionsModal = (payment: IBankTransferPayment) => {
    create((modalProps) => (
      <BankTransferInstructionsModal {...modalProps} payment={payment} />
    ));
  };

  const openPaperBillPaymentModal = (payment: IPaperBillPayment) => {
    create((modalProps) => (
      <PaperBillPaymentModal
        {...modalProps}
        payment={payment}
        periodId={periodId}
      />
    ));
  };

  const redirectToOnlinePayment = (payment: IOnlinePayment) => {
    if (payment.paymentUrl) {
      window.location.href = payment.paymentUrl;
    }
  };

  const openAMLModal = () => {
    create((modalProps) => (
      <AMLModal {...modalProps} onFormSubmit={() => submitPayment(true)} />
    ));
  };

  const openPaymentRestrictionModal = () => {
    create((modalProps) => (
      <PaymentRestrictionModal
        title={t(
          `paymentRestrictionModal.${customerMetadata?.paymentRestrictionTypeId}.title`,
        )}
        description={t(
          `paymentRestrictionModal.${customerMetadata?.paymentRestrictionTypeId}.description`,
        )}
        {...modalProps}
      />
    ));
  };

  const openKYCPdfModal = () => {
    create((modalProps) => <KYCWarningModal {...modalProps} />);
  };

  const setValidationErrors = (errors: IValidationError[]) => {
    const meterReadingErrors = errors
      ?.filter((err) => typeof err.additionalData?.billDetailId === typeof 0)
      .map((err) => ({
        billDetailId: err.additionalData?.billDetailId as number,
        fieldName: err.fieldName,
        text: err.text,
      }));
    const billNumberErrors = errors
      ?.filter(
        (err) =>
          typeof err.additionalData?.billId === typeof 0 &&
          typeof err.additionalData?.jointBillServiceId === typeof 0 &&
          err.fieldName === "billNumber",
      )
      .map((err) => ({
        jointBillServiceId: err.additionalData?.jointBillServiceId as number,
        billId: err.additionalData?.billId as number,
        fieldName: err.fieldName,
        text: err.text,
      }));

    setMeterReadingErrors(meterReadingErrors);
    setBillNumberErrors(billNumberErrors);

    if (!meterReadingErrors?.length && !billNumberErrors?.length) {
      toast.error(t("common.unknownErrorOccurred"));
    }
  };

  const clearErrors = () => {
    clearMeterReadingErrors();
    clearBillNumberErrors();
  };

  const submitPayment = async (skipBeforePaymentAction = false) => {
    clearErrors();
    await Promise.resolve();

    if (!skipBeforePaymentAction) {
      // #region payment restriction
      if (
        customerMetadata?.paymentRestrictionTypeId ===
        PaymentRestrictionType.NotFilledKYC
      ) {
        openKYCPdfModal();
        return;
      }

      if (
        customerMetadata?.paymentRestrictionTypeId ===
        PaymentRestrictionType.DidNotProvideDocuments
      ) {
        openPaymentRestrictionModal();
        return;
      }

      if (
        customerMetadata?.paymentRestrictionTypeId ===
        PaymentRestrictionType.ExtremeRiskLevel
      ) {
        openPaymentRestrictionModal();
        return;
      }

      if (
        customerMetadata?.beforePaymentAction ===
        BeforePaymentAction.FilloutAntiMoneyLaunderingForm
      ) {
        openAMLModal();
        return;
      }

      if (
        customerMetadata?.beforePaymentAction ===
        BeforePaymentAction.FilloutKycWithAntiMoneyLaunderingForm
      ) {
        openKYCPdfModal();
        return;
      }

      if (
        customerMetadata?.beforePaymentAction ===
        BeforePaymentAction.RestrictPayment
      ) {
        openKYCPdfModal();
        return;
      }
      // #endregion
    }

    const payment = await initiatePayment({
      periodId,
      selectedJointBillServiceIds,
    }).catch((err) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setValidationErrors(getApiErrors(err));
      return;
    });

    if (payment) {
      switch (payment.paymentType) {
        case PaymentType.ONLINE_PAYMENT:
          redirectToOnlinePayment(payment as IOnlinePayment);
          return;
        case PaymentType.BANK_TRANSFER:
          openBankTransferInstructionsModal(payment as IBankTransferPayment);
          return;
        case PaymentType.PAPER_BILL:
          openPaperBillPaymentModal(payment as IPaperBillPayment);
          return;
        default:
          return;
      }
    }
  };

  useEffect(() => () => clearMeterReadingErrors(), [clearMeterReadingErrors]);

  const getPaymentButtonLabel = () => {
    return t("billsPayment.button", {
      amount: !isNullOrUndefined(totalAmount)
        ? formatPrice(totalAmount)
        : undefined,
    });
  };

  return (
    <div className="mt-6 flex flex-col gap-4 px-4 md:px-0">
      <div className="flex flex-col gap-4">
        {!!meterReadingErrors.length && (
          <BillErrorsNotification
            description={t(
              "billsPayment.errors.incorrectMeterReadings.description.multiple",
            )}
            buttonText={t("billsPayment.errors.incorrectMeterReadings.button")}
            formId="InvalidBillDetailForm"
          />
        )}
        {!!billNumberErrors.length && (
          <BillErrorsNotification
            description={t("billsPayment.errors.billNumber.description")}
            buttonText={t("billsPayment.errors.billNumber.button")}
            formId="InvalidBillNumberForm"
          />
        )}
      </div>
      <FeatureFlag flag={["isTaxCollectorRadioEnabled"]}>
        <div className="flex justify-end">
          <TaxCollectorRadio
            paymentTaxCollectorData={paymentTaxCollectorData}
            currentPaymentTaxCollector={currentPaymentTaxCollector}
          />
        </div>
      </FeatureFlag>
      <div className="flex flex-col items-end justify-end gap-5 md:flex-row md:items-center">
        <FeatureFlag flag={["isTaxCollectorDisplayWithModalEnabled"]}>
          <SelectedTaxCollectorDisplay
            currentPaymentTaxCollector={currentPaymentTaxCollector}
            isPaymentSectionLodaing={isLoading}
          />
        </FeatureFlag>
        <Button
          className="h-12 text-base"
          isLoading={isLoading}
          onClick={() => submitPayment()}
          disabled={isNullOrUndefined(totalAmount) || (totalAmount ?? 0) <= 0}
          aria-label={getPaymentButtonLabel()}
        >
          {getPaymentButtonLabel()}
        </Button>
      </div>
      {/* For Operator in a fake session we show fake payment response info */}
      {paymentInitiationData?.paymentType === PaymentType.FAKE_PAYMENT && (
        <FakePaymentInformationCard
          paymentData={paymentInitiationData as IFakePayment}
        />
      )}
    </div>
  );
};
